/**
 * StarRating - Interactive 1-5 star rating component
 *
 * Provides a visual star rating interface with hover preview and click selection.
 */

import { useState } from 'react'
import { Star } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'

interface StarRatingProps {
  value: number | null
  onChange: (rating: number) => void
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
}

export function StarRating({
  value,
  onChange,
  disabled = false,
  size = 'md',
}: StarRatingProps) {
  const { t } = useTranslation()
  const [hoverValue, setHoverValue] = useState<number | null>(null)

  const displayValue = hoverValue ?? value ?? 0

  const handleClick = (rating: number) => {
    if (!disabled) {
      onChange(rating)
    }
  }

  const handleMouseEnter = (rating: number) => {
    if (!disabled) {
      setHoverValue(rating)
    }
  }

  const handleMouseLeave = () => {
    setHoverValue(null)
  }

  return (
    <div
      className={clsx(
        'flex gap-1',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onMouseLeave={handleMouseLeave}
    >
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          key={rating}
          type="button"
          onClick={() => handleClick(rating)}
          onMouseEnter={() => handleMouseEnter(rating)}
          disabled={disabled}
          className={clsx(
            'p-0.5 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-brand',
            !disabled && 'hover:scale-110 cursor-pointer',
            disabled && 'cursor-not-allowed'
          )}
          aria-label={t('chatv2:feedback.rate_n_stars', { rating, defaultValue: 'Rate {{rating}} out of 5 stars' })}
        >
          <Star
            className={clsx(
              sizeClasses[size],
              'transition-colors',
              rating <= displayValue
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-none text-gray-300 dark:text-gray-600'
            )}
          />
        </button>
      ))}
    </div>
  )
}

export default StarRating
